import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Modal, Button } from './index';
import './TwoFactorPromptModal.css';

const STORAGE_KEY = 'naijanest_2fa_prompt';
const SNOOZE_DAYS = 14;
// Small delay so the modal doesn't compete with the page's own first paint
// right after login.
const SHOW_DELAY_MS = 2500;

function readDismissals() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeDismissal(userId, record) {
  try {
    const all = readDismissals();
    all[userId] = record;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // localStorage unavailable - the modal may just show again next time,
    // non-critical
  }
}

function isDismissed(userId) {
  const record = readDismissals()[userId];
  if (!record) return false;
  // No permanent dismissal - security reminders like this one shouldn't be
  // silenceable forever, only snoozed. (Older stored records may still
  // have dismissedForever from before this was removed; deliberately not
  // honored anymore.)
  if (record.snoozedUntil && new Date(record.snoozedUntil) > new Date()) return true;
  return false;
}

/**
 * Nudges signed-in users who haven't enabled 2FA to do so. Shown once per
 * qualifying app load (not gated behind cookie consent - this is a
 * first-party UX preference stored the same way as other local-only UI
 * state in this app, not a third-party script/tracker like Google One Tap).
 */
export default function TwoFactorPromptModal({ disabled = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (disabled || !user || user.twoFactorEnabled) {
      return undefined;
    }
    if (isDismissed(user.id)) {
      return undefined;
    }
    const timer = setTimeout(() => setIsOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [disabled, user]);

  if (!user || user.twoFactorEnabled) {
    return null;
  }

  const close = () => setIsOpen(false);

  const handleRemindLater = () => {
    const snoozedUntil = new Date();
    snoozedUntil.setDate(snoozedUntil.getDate() + SNOOZE_DAYS);
    writeDismissal(user.id, { snoozedUntil: snoozedUntil.toISOString() });
    close();
  };

  const handleSetUpNow = () => {
    close();
    navigate('/security');
  };

  return (
    <Modal isOpen={isOpen} onClose={handleRemindLater} title="Secure your account" size="sm">
      <p className="two-factor-prompt__body">
        You don&rsquo;t have two-factor authentication enabled. Add an extra layer of protection
        to your account in under a minute.
      </p>
      <div className="two-factor-prompt__actions">
        <button type="button" className="btn-link" onClick={handleRemindLater}>
          Remind me later
        </button>
        <Button variant="primary" onClick={handleSetUpNow}>
          Set up 2FA now
        </Button>
      </div>
    </Modal>
  );
}
